import * as _ from 'lodash';
import {combineLatest, Observable, of, OperatorFunction} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {catchError, filter, map, switchMap, take, withLatestFrom} from 'rxjs/operators';

import {TranslateService} from '@ngx-translate/core';
import {
  Field,
  FieldType,
  fieldType_getDefaultOperator,
  fieldType_getOperators,
  SearchOperator
} from '../../../../core/models/field';
import {SearchItem} from '../../../../core/models/search-item';
import {SearchArgument, State} from '../../../../core/models/search-argument';
import {EsPage} from '../../../../core/models/es-page';
import {Url} from '../../../../core/models/url';
import {SearchPath} from '../../../../core/models/search-path';
import {ObjectType} from '../../../../core/models/ObjectType';
import {DocumentSearchResultService} from '../../../../core/redux/search-result/service/document-search-result.service';
import {EntitySearchResultService} from '../../../../core/redux/search-result/service/entity-search-result.service';
import {AbstractSearchResultService} from '../../../../core/redux/search-result/service/abstract-search-result.service';
import {GedCfgFieldService} from '../../../../core/redux/field/service/ged/ged-cfg-field.service';
import {WkfCfgFieldService} from '../../../../core/redux/field/service/wkf/wkf-cfg-field.service';
import {TaskSearchResultService} from '../../../../core/redux/search-result/service/task-search-result.service';
import { ConfigurationService } from '../../../configuration/configuration.service';
import {StringUtils} from '../../../../core/utils/string-utils';

@Injectable({
  providedIn: 'root'
})
export abstract class AbstractAutocompletionService {

  public static readonly AUTOCOMPLETION_MIN_LENGTH = 1;
  public static readonly PREFIX_KEYWORD = ['#', '$'];
  protected apiAutocompleteByKeyword: string;
  protected apiAutocompleteByValue: string;
  protected apiAutocompleteByKeyValue: string;
  protected apiAutocompleteAggregateByKeyword: string;

  protected static STATIC_KW_SEARCH_ITEM: {[index: string]: SearchItem} = {
    // TODO : et en anglais ?
    'equipe': new SearchItem(
      1,
      'Équipes',
      'cdx_classification.teams',
      null,
      true,
      FieldType.STRING
    )
  }

  constructor(
    protected http: HttpClient,
    protected translateService: TranslateService,
    protected configAction: ConfigurationService
  ) {
  }

  public static hasPrefix(fragment: string): boolean {
    if (!!fragment) {
      return AbstractAutocompletionService.PREFIX_KEYWORD.indexOf(fragment.substring(0, 1)) > -1;
    } else {
      return false;
    }
  }

  private static normalizeString(str: string): string {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private static isTeamKeyword(fragment: string): boolean {
    if (!fragment) {
      return false;
    }
    const normalized = AbstractAutocompletionService.normalizeString(fragment);
    return normalized === 'equ' || normalized === 'equi' || normalized === 'equipe' || normalized === 'equipes';
  }

  public search(arg: SearchArgument, entityType: string = null): Observable<{ [key: string]: EsPage<SearchItem> }> {
    const path = arg.path;
    let fragment = arg.fragment;
    if (null != fragment) {
      fragment = fragment.trim();
    }
    if (arg.state === State.OPERATOR) {
      return of(this.getOperators(arg));
    }

    if ((!path && !fragment)) {
      return of({});
    }
    const hasKeyword = AbstractAutocompletionService.hasPrefix(fragment);
    if (!!path) {
      return this.searchByKeywordValue(fragment, path, entityType, arg?.searchItem?.operator);
    } else if (hasKeyword) {
      return this.searchByKeyword(fragment.substr(1), entityType);
    } else {
      return this.searchByValue(fragment, entityType);
    }
  }

  // utilisé quand un autre environnement (workflow) a besoin d'utiliser l'api de la ged
  // exemple : appel à l'api SEARCH_LIST lorsque $ est tapé en autocompletion dans le champ de recherche workflow
  private getGedApiBaseUrl() : string {
    return Url.getProtectedApiBaseUrl(this.configAction);
  }

  searchByHistory(entityType: string = null): Observable<{ [key: string]: EsPage<SearchItem> }> {
    // this.currentWorkspaceItems = this.workspaceItemsList.filter(x => x.current === true);
    const historypath$ = (entityType ?
      this.http.get<string []>(this.getGedApiBaseUrl() + Url.LINKS + entityType + '/' + Url.SEARCH_LIST) :
      this.http.get<string []>(this.getGedApiBaseUrl() + Url.DOC_TYPES + Url.AGGREGATES + Url.SEARCH_LIST))
      .pipe(
        map((paths: string[]) => {
          return paths.map(path => new SearchPath(path));
        })
      );

    return combineLatest([
      historypath$,
      this.getCfgConfigStore()])
      .pipe(
        filter((val: [SearchPath [], { [key: string]: Field }]) => {
          const unknownCode = [];
          const paths = val[0];
          const gedFields = val[1];
          if (!gedFields) {
            this.getCfgFieldService().loadFieldsFromPath(...paths.map(searchPath => searchPath.code));
            return false;
          }
          unknownCode.push(...paths.map(searchPath => searchPath.code)
            .filter(code => !gedFields[code]));
          if (unknownCode.length > 0) {
            this.getCfgFieldService().loadFields(...unknownCode);
            return false;
          }
          return true;
        }),

        // filter((val: [string [], {[key: string]: Field}]) => {
        //   const unknownCode = [];
        //   const paths = val[0];
        //   const fields = val[1];
        //   if (!fields) {
        //     this.fieldService.loadFieldsFromPath(...paths);
        //     return false;
        //   }
        //   paths.forEach(path => {
        //     path = path.substring(path.lastIndexOf('.') + 1, path.length);
        //     if (!fields[path])  {
        //       unknownCode.push(path);
        //     }
        //   });
        //   if (unknownCode.length > 0) {
        //     this.fieldService.loadFields(...unknownCode);
        //     return false;
        //   }
        //   return true;
        // }),
        map((val: [SearchPath [], { [key: string]: Field }]) => {
          const paths = val[0];
          const fields = val[1];
          const searchItems: SearchItem[] = paths.map((path: SearchPath) => {
            const label: string = fields[path.code]?.label[this.translateService.currentLang];
            // return new SearchItem(0, label, path.path, null, true, FieldType.STRING);
            const modifSearchItem: SearchItem = new SearchItem(0, label, path.path, null, true, FieldType.STRING);
            modifSearchItem.fieldType = path.isMeta ? SearchPath.getMetaDatasFieldType(path.code) : fields[path.code] ? fields[path.code].type : undefined;
            return modifSearchItem;
          });

          // Filtrer les équipes de l'historique - elles ne doivent apparaître que via le keyword "équipe"
          const filteredSearchItems = searchItems.filter(item => !item.path || !item.path.includes('teams'));

          const esMap: { [key: string]: EsPage<SearchItem> } = {
            'keyword': {
              'content': filteredSearchItems,
              'size': filteredSearchItems.length,
              'numberOfElements': filteredSearchItems.length,
              'page': 0,
              'aggs': null,
              'lowerBounded': false
            }
          };
          return esMap;
        })
      );
  }

  searchByKeyword(fragment: string, entityType: string = null, autocompleteType: ObjectType = ObjectType.DOCUMENT, isAggregate = false): Observable<{
    [key: string]: EsPage<SearchItem>
  }> {
    if (!fragment || fragment.length === 0) {
      return this.searchByHistory(entityType);
    }
    const service: AbstractSearchResultService = this._getCurrentSearchResultService(entityType);
    return of(new HttpParams()).pipe(
      service.workspaceParams(),
      this.autocompletionParams(fragment),
      take(1),
      switchMap((httpParams: HttpParams) => {
        if (!!entityType) {
          httpParams = httpParams.append('_entityType', entityType);
        }
        httpParams = httpParams.append('_autocompleteType', autocompleteType);
        const searchByKeywordUrl =(isAggregate ? this.apiAutocompleteAggregateByKeyword : this.apiAutocompleteByKeyword)
        console.debug('-> AbstractAutocompletionService .searchByKeyword(),  this.apiAutocompleteAggregateByKeyword : ',this.apiAutocompleteAggregateByKeyword);
        console.debug('-> AbstractAutocompletionService .searchByKeyword(),  this.apiAutocompleteByKeyword : ',this.apiAutocompleteByKeyword);
        console.debug('---> AbstractAutocompletionService .searchByKeyword(),  searchByKeywordUrl (isAggregate='+isAggregate+') : ',searchByKeywordUrl);
        return this.http
          .get<{
            [key: string]: EsPage<SearchItem>
          }>(searchByKeywordUrl, {params: httpParams})
          .pipe(
            map((searchItemPage: { [key: string]: EsPage<SearchItem> }) => {
              if (isAggregate && fragment.length > 0) {
                // On complète avec les SearchItem static
                const normalizedFragment = StringUtils.toASCII(fragment.toLowerCase());
                Object.keys(AbstractAutocompletionService.STATIC_KW_SEARCH_ITEM)
                  .filter(key => key.toLowerCase().includes(normalizedFragment))
                  .forEach(key => searchItemPage['keyword'].content.push(AbstractAutocompletionService.STATIC_KW_SEARCH_ITEM[key]))
                searchItemPage['keyword'].size = searchItemPage['keyword'].content.length;
                searchItemPage['keyword'].numberOfElements = searchItemPage['keyword'].content.length;
              }
              return searchItemPage;
            }),
            map((searchItemsPage: { [key: string]: EsPage<SearchItem> }) => {
              const searchItemsKeys: string[] = Object.keys(searchItemsPage);
              if (searchItemsKeys && searchItemsKeys.length) {
                const fieldCodesToSearchFor: string[] = [];
                searchItemsKeys.forEach((key: string) => {
                  if (!searchItemsPage[key].content || !searchItemsPage[key].content.length) {
                    return;
                  }
                  searchItemsPage[key].content.forEach((searchItem: SearchItem) => {
                    const searchPath: SearchPath = new SearchPath(searchItem.path);
                    !searchPath.isMeta ? fieldCodesToSearchFor.push(searchPath.code) : searchItem.fieldType = SearchPath.getMetaDatasFieldType(searchPath.code);
                  });
                });
                if (fieldCodesToSearchFor.length) {
                  return this.getCfgFieldService().loadFields(...fieldCodesToSearchFor).pipe(
                    map((fields: { [key: string]: Field }) => {
                      searchItemsKeys.forEach((key: string) => {
                        searchItemsPage[key].content.forEach((searchItem: SearchItem) => {
                          const searchPath: SearchPath = new SearchPath(searchItem.path);
                          if (!searchPath.isMeta) {
                            searchItem.fieldType = fields[searchPath.code].type;
                          }
                        });
                      });
                      return searchItemsPage;
                    })
                  );
                } else {
                  return of(searchItemsPage);
                }
              } else {
                return of(searchItemsPage);
              }
            }),
            switchMap((obs$) => {
              return obs$.pipe(
                // define default operator
                map((searchItemPage: { [key: string]: EsPage<SearchItem> }) => {
                  searchItemPage?.['keyword']?.content?.forEach(searchItem => {
                    if (!!searchItem.fieldType) {
                      searchItem.operator = fieldType_getDefaultOperator(searchItem.fieldType);
                    }
                  });
                  return searchItemPage;
                })
              );
            }),
            catchError((err) => {
              console.error(err);
              return of({});
            })
          );
      })
    );
  }

  searchByValue(fragment: string, entityType: string = null): Observable<{ [p: string]: EsPage<SearchItem> }> {
    const autocompleteType: ObjectType = ObjectType.DOCUMENT;
    if (fragment.length < AbstractAutocompletionService.AUTOCOMPLETION_MIN_LENGTH) {
      return of({});
    }
    const service: AbstractSearchResultService = this._getCurrentSearchResultService(entityType);
    return of(new HttpParams()).pipe(
      service.workspaceParams(),
      service.coordParams(25, 0),
      this.autocompletionParams(fragment),
      take(1),
      switchMap((httpParams: HttpParams) => {
        if (!!entityType) {
          httpParams = httpParams.append('_entityType', entityType);
        }
        httpParams = httpParams.append('_autocompleteType', autocompleteType);
        return this.http
          .get<{ [key: string]: EsPage<SearchItem> }>(this.apiAutocompleteByValue, {params: httpParams})
          .pipe(
            // load default operators for keyword_value part of the response
            switchMap((searchItemPage: { [key: string]: EsPage<SearchItem> }) => {
              const fieldCodesToSearchFor: string[] = [];
              const searchPaths: SearchPath[] = [];
              searchItemPage['keyword_value']?.content?.forEach(item => {
                if (!!item.fieldType) {
                  item.operator = fieldType_getDefaultOperator(item.fieldType);
                } else {
                  if (item.path) {
                    const searchPath: SearchPath = new SearchPath(item.path);
                    if (!searchPath.isMeta) {
                      fieldCodesToSearchFor.push(searchPath.code);
                      searchPaths.push(searchPath);
                    } else {
                      item.fieldType = SearchPath.getMetaDatasFieldType(searchPath.code);
                    }
                  }
                }
              });
              if (fieldCodesToSearchFor.length) {
                return this.getCfgFieldService().loadFields(...fieldCodesToSearchFor).pipe(
                  map((fields: { [key: string]: Field }) => {
                    searchPaths.forEach((searchPath: SearchPath) => {
                      searchItemPage['keyword_value']?.content?.map((item: SearchItem) => {
                        if (item.path === searchPath.path) {
                          item.fieldType = fields[searchPath.code].type;
                          item.operator = fieldType_getDefaultOperator(item.fieldType);
                        }
                      });
                    });
                    return searchItemPage;
                  })
                );
              }
              return of(searchItemPage);
            }),
            map((searchItemPage: { [key: string]: EsPage<SearchItem> }) => {
              console.log(searchItemPage);
              return searchItemPage;
            }),
            catchError(err => {
              // TODO manage errors
              console.error(err)
              return of({});
            })
          );
      })
    );
  }

  searchByKeywordValue(fragment: string, path: string, entityType: string = null, operator: SearchOperator | undefined): Observable<{
    [p: string]: EsPage<SearchItem>
  }> {
    const autocompleteType: ObjectType = ObjectType.DOCUMENT;
    //const docOrEntity: InternalRoutes = entityType ? InternalRoutes.ENTITIES : InternalRoutes.DOCUMENTS;
    if (!path || path.length === 0) {
      return of({});
    }
    const searchPath: SearchPath = new SearchPath(path);
    const fields$: Observable<{
      [key: string]: Field
    }> = searchPath.isMeta ? of({}) : this.getCfgFieldService().loadFields(searchPath.code);
    const service: AbstractSearchResultService = this._getCurrentSearchResultService(entityType);
    return of(new HttpParams()).pipe(
      service.workspaceParams(),
      service.searchItemsParams(path),
      service.filterItemsParams(null),
      // TODO maybe add loadmore method
      service.coordParams(25, 0),
      this.autocompletionParams(fragment, path),
      take(1),
      switchMap((httpParams: HttpParams) => {
        if (!!entityType) {
          httpParams = httpParams.append('_entityType', entityType);
        }
        httpParams = httpParams.append('_autocompleteType', autocompleteType);
        return this.http
          .get<{ [key: string]: EsPage<SearchItem> }>(this.apiAutocompleteByKeyValue, {params: httpParams})
          .pipe(
            withLatestFrom(fields$),
            // define type and operator
            map(([searchItemPage, fields]: [{ [key: string]: EsPage<SearchItem> }, { [key: string]: Field }]) => {
              const type: FieldType = searchPath.isMeta ? SearchPath.getMetaDatasFieldType(searchPath.code) : fields[searchPath.code] ? fields[searchPath.code].type : undefined;
              const defaultOperator = operator ? operator : fieldType_getDefaultOperator(type);
              searchItemPage?.['keyword_value']?.content.forEach(searchItem => {
                  searchItem.fieldType = type;
                  searchItem.operator = defaultOperator;
                }
              );
              return searchItemPage;
            }),
            catchError(err => {
              console.error(err)
              return of({});
            })
          );
      })
    );
  }

  protected abstract getApiBaseUrl(): string;

  protected abstract getCfgFieldService(): GedCfgFieldService | WkfCfgFieldService;

  protected abstract getCurrentSearchResultService(entityType: string): DocumentSearchResultService | EntitySearchResultService | TaskSearchResultService;

  protected abstract getCfgConfigStore(): Observable<{ [key: string]: Field }>;

  private _getCurrentSearchResultService(entityType: string): AbstractSearchResultService {
    return this.getCurrentSearchResultService(entityType);
  }

  private autocompletionParams(fragment?: string, path?: string): OperatorFunction<HttpParams, HttpParams> {
    return map((params: HttpParams) => {
      if (!!fragment) {
        params = params.append('_fragment', fragment.trim());
      }
      if (!!path) {
        params = params.append('_path', path);
      }
      return params;
    });
  }

  private getOperators(arg: SearchArgument): { [key: string]: EsPage<SearchItem> } {
    let isOperatorsForValue = false;
    if (arg.searchItem && !arg.path) {
      isOperatorsForValue = true;
    }
    const searchItems: SearchItem[] = fieldType_getOperators(arg.fieldType, isOperatorsForValue).map( operator => {
      const item = _.cloneDeep(arg.searchItem);
      item.operator = operator;
      return item;
    });
    //const result: { [key: string]: EsPage<SearchItem> } = {};
    const esPage: EsPage<SearchItem> = new EsPage(searchItems.length, searchItems, searchItems.length, 0, [], false);
    return {
      'operator': esPage
    };
  }
}
